import { getScrollTop, getScrollEventTarget } from '@theme/scroll-container';

// Wordmark morph for the home-page hero. See blocks/wordmark-morph.liquid.

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const MOBILE = window.matchMedia('(max-width: 749px)');

/**
 * Scales the hero wordmark (the store logo at hero size) down on scroll so it
 * lands on the header's logo slot, then hands off to the real header logo.
 *
 * Progress is tied to scroll, not time: the wordmark's untransformed box drifts
 * up with the page, and it scales around its own centre so that centre reaches
 * the logo slot's centre exactly when scale reaches the logo's height. Nothing
 * here is animated on a timer — scrolling back reverses it.
 *
 * States, mirrored onto `#header-component[data-wordmark-morph]` for the CSS:
 *   pending server-rendered initial state; CSS hides the header logo until we run
 *   hero    wordmark travelling; header kept see-through, its logo hidden
 *   header  handed off; header back to Horizon's normal sticky state
 *   static  morph disabled or reduced motion; header untouched
 */
class WordmarkMorph extends HTMLElement {
  /** @type {HTMLElement | null} */
  #header = null;
  /** @type {HTMLElement | null} */
  #logo = null;
  /** @type {HTMLElement | null} */
  #mark = null;
  /** @type {{ top: number, height: number, centerX: number } | null} */
  #natural = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {EventTarget | null} */
  #scrollTarget = null;
  #frame = 0;

  connectedCallback() {
    this.#header = document.querySelector('#header-component');
    this.#logo = this.#header?.querySelector('.header-logo') ?? null;
    this.#mark = this.querySelector('[data-wordmark-mark]');

    const enabled = this.dataset.morph === 'true' && !REDUCED_MOTION.matches;

    if (!enabled || !this.#header || !this.#logo || !this.#mark) {
      this.dataset.state = 'static';
      return;
    }

    this.#setState('hero');

    // The image sizes itself as it loads and on viewport changes; re-measure whenever its box settles.
    this.#resizeObserver = new ResizeObserver(() => this.#measure());
    this.#resizeObserver.observe(this.#mark);

    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#requestUpdate, { passive: true });
    window.addEventListener('resize', this.#requestUpdate);

    this.#measure();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#scrollTarget?.removeEventListener('scroll', this.#requestUpdate);
    window.removeEventListener('resize', this.#requestUpdate);
    if (this.#frame) cancelAnimationFrame(this.#frame);
    if (this.#header) delete this.#header.dataset.wordmarkMorph;
  }

  /**
   * Records the wordmark's untransformed box in scroll-container coordinates.
   * Transform variables are reset for the measurement and restored before paint.
   */
  #measure() {
    const mark = this.#mark;
    if (!mark) return;

    const previousScale = mark.style.getPropertyValue('--wm-scale');
    const previousDx = mark.style.getPropertyValue('--wm-dx');
    mark.style.setProperty('--wm-scale', '1');
    mark.style.setProperty('--wm-dx', '0px');

    // The landing target is the part of the mark that docks into the header
    // (the lettering of a lockup); with no target the whole mark lands.
    const target = mark.querySelector('[data-wordmark-target]') ?? mark;
    const markRect = mark.getBoundingClientRect();
    const rect = target.getBoundingClientRect();

    // Scale around the target's centre so it is the point that travels.
    if (target !== mark && markRect.width && markRect.height) {
      const ox = rect.left + rect.width / 2 - markRect.left;
      const oy = rect.top + rect.height / 2 - markRect.top;
      mark.style.setProperty('--wm-origin', `${ox.toFixed(1)}px ${oy.toFixed(1)}px`);
    }

    mark.style.setProperty('--wm-scale', previousScale);
    mark.style.setProperty('--wm-dx', previousDx);

    if (rect.height === 0) {
      this.#natural = null;
      return;
    }

    this.#natural = {
      top: rect.top + getScrollTop(),
      height: rect.height,
      centerX: rect.left + rect.width / 2,
    };

    this.#update();
  }

  #requestUpdate = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #update() {
    const natural = this.#natural;
    const mark = this.#mark;
    const logo = this.#logo;
    if (!natural || !mark || !logo) return;

    const scrollTop = getScrollTop();

    // The slot is measured live: the header section is sticky, so once stuck
    // this is stable, and before that it simply tracks wherever the header is.
    // Aim at the mark itself (image or text) rather than the padded link around it.
    const logoMark = logo.querySelector('.header-logo__image, .header-logo__text');
    const logoRect = (logoMark ?? logo).getBoundingClientRect();
    const configuredHeight = Number(MOBILE.matches ? this.dataset.targetHeightMobile : this.dataset.targetHeight);
    const targetHeight = logoRect.height || configuredHeight || 36;
    const targetCenterY = logoRect.top + logoRect.height / 2;
    const targetCenterX = logoRect.width ? logoRect.left + logoRect.width / 2 : window.innerWidth / 2;

    const endScale = Math.min(1, targetHeight / natural.height);

    // Scroll distance at which the wordmark's (untransformed) centre reaches the slot's centre.
    const distance = natural.top + natural.height / 2 - targetCenterY;
    const progress = distance > 0 ? Math.min(Math.max(scrollTop / distance, 0), 1) : 1;

    const scale = 1 + (endScale - 1) * progress;
    const dx = (targetCenterX - natural.centerX) * progress;

    // Anything marked as fading (the emblem of a lockup) is gone by two thirds of the way.
    const fade = Math.max(0, 1 - progress * 1.5);

    mark.style.setProperty('--wm-scale', scale.toFixed(4));
    mark.style.setProperty('--wm-dx', `${dx.toFixed(1)}px`);
    mark.style.setProperty('--wm-fade', fade.toFixed(3));
    this.toggleAttribute('data-morphing', progress > 0 && progress < 1);

    this.#setState(progress >= 0.995 ? 'header' : 'hero');
  }

  /** @param {'hero' | 'header'} state */
  #setState(state) {
    if (this.dataset.state === state) return;
    this.dataset.state = state;
    if (this.#header) this.#header.dataset.wordmarkMorph = state;
  }
}

if (!customElements.get('wordmark-morph')) {
  customElements.define('wordmark-morph', WordmarkMorph);
}
